import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ImageUploader } from '../components/ImageUploader';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, X, Languages, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { RichTextEditor } from '../components/RichTextEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProduct = {
  name_nl: '', name_en: '', name_fr: '', name_de: '',
  description_nl: '', description_en: '', description_fr: '', description_de: '',
  category_id: '', image_url: '', specifications: [], is_active: true,
};

/* ── Group products by base name (same logic as frontend) ── */
function groupProducts(products) {
  const groups = {};
  products.forEach((p) => {
    const name = p.name_nl || '';
    let base = name
      .replace(/\s*L=[\d,]+M$/i, '')
      .replace(/\s*[\d,]+x[\d,]+(mm)?$/i, '')
      .replace(/\s*Ø[\d,]+mm$/i, '')
      .replace(/\s*Size\s*\d+$/i, '')
      .replace(/\s*(2xPack|set\s.*)$/i, '')
      .trim();
    const variant = name.replace(base, '').trim();
    if (!groups[base]) groups[base] = { base, products: [], variants: [] };
    groups[base].products.push(p);
    if (variant) groups[base].variants.push({ label: variant, product: p });
  });
  return Object.values(groups);
}

export const AdminProductsPage = () => {
  const { getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/products?active_only=false`, { headers: getAuthHeader() }),
        axios.get(`${API}/categories`)
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAdd = () => { setSelectedProduct(null); setFormData(emptyProduct); setDialogOpen(true); };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      name_nl: product.name_nl || '', name_en: product.name_en || '', name_fr: product.name_fr || '', name_de: product.name_de || '',
      description_nl: product.description_nl || '', description_en: product.description_en || '', description_fr: product.description_fr || '', description_de: product.description_de || '',
      category_id: product.category_id || '', image_url: product.image_url || '',
      specifications: product.specifications || [], is_active: product.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleDelete = (product) => { setSelectedProduct(product); setDeleteDialogOpen(true); };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/admin/products/${selectedProduct.id}`, { headers: getAuthHeader() });
      toast.success('Product verwijderd');
      fetchData();
    } catch { toast.error('Fout bij verwijderen'); }
    finally { setDeleteDialogOpen(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category_id) { toast.error('Selecteer een categorie'); return; }
    setSaving(true);
    try {
      if (selectedProduct) {
        await axios.put(`${API}/admin/products/${selectedProduct.id}`, formData, { headers: getAuthHeader() });
        toast.success('Product bijgewerkt');
      } else {
        await axios.post(`${API}/admin/products`, formData, { headers: getAuthHeader() });
        toast.success('Product toegevoegd');
      }
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Fout bij opslaan'); }
    finally { setSaving(false); }
  };

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const addSpec = () => setFormData(prev => ({ ...prev, specifications: [...prev.specifications, { key_nl: '', key_en: '', key_fr: '', key_de: '', value: '' }] }));
  const updateSpec = (i, f, v) => setFormData(prev => ({ ...prev, specifications: prev.specifications.map((s, idx) => idx === i ? { ...s, [f]: v } : s) }));
  const removeSpec = (i) => setFormData(prev => ({ ...prev, specifications: prev.specifications.filter((_, idx) => idx !== i) }));

  /* ── AI Translation ── */
  const translateAll = async () => {
    if (!formData.name_nl) { toast.error('Vul eerst de Nederlandse naam in'); return; }
    setTranslating(true);
    try {
      // Translate name
      const nameRes = await axios.post(`${API}/admin/translate`, {
        text: formData.name_nl, source_lang: 'nl', target_langs: ['en', 'fr', 'de']
      }, { headers: getAuthHeader() });

      const nameT = nameRes.data.translations;
      const updates = {
        name_en: nameT.en || formData.name_en,
        name_fr: nameT.fr || formData.name_fr,
        name_de: nameT.de || formData.name_de,
      };

      // Translate description if present
      if (formData.description_nl && formData.description_nl.replace(/<[^>]*>/g, '').trim()) {
        const descRes = await axios.post(`${API}/admin/translate`, {
          text: formData.description_nl, source_lang: 'nl', target_langs: ['en', 'fr', 'de']
        }, { headers: getAuthHeader() });
        const descT = descRes.data.translations;
        updates.description_en = descT.en || formData.description_en;
        updates.description_fr = descT.fr || formData.description_fr;
        updates.description_de = descT.de || formData.description_de;
      }

      // Translate spec keys
      if (formData.specifications.length > 0) {
        const specKeys = formData.specifications.map(s => s.key_nl).filter(Boolean).join(' | ');
        if (specKeys) {
          const specRes = await axios.post(`${API}/admin/translate`, {
            text: specKeys, source_lang: 'nl', target_langs: ['en', 'fr', 'de']
          }, { headers: getAuthHeader() });
          const specT = specRes.data.translations;
          const enKeys = (specT.en || '').split(' | ');
          const frKeys = (specT.fr || '').split(' | ');
          const deKeys = (specT.de || '').split(' | ');
          updates.specifications = formData.specifications.map((s, i) => ({
            ...s,
            key_en: enKeys[i]?.trim() || s.key_en,
            key_fr: frKeys[i]?.trim() || s.key_fr,
            key_de: deKeys[i]?.trim() || s.key_de,
          }));
        }
      }

      setFormData(prev => ({ ...prev, ...updates }));
      toast.success('Vertalingen succesvol gegenereerd!');
    } catch (err) {
      console.error(err);
      toast.error('Vertaling mislukt');
    } finally { setTranslating(false); }
  };

  const getCategoryName = (cid) => categories.find(c => c.id === cid)?.name_nl || '-';

  const toggleGroup = (base) => setExpandedGroups(prev => ({ ...prev, [base]: !prev[base] }));

  /* ── Filtered and grouped products ── */
  const filteredProducts = filterCategory === 'all' ? products : products.filter(p => p.category_id === filterCategory);
  const grouped = groupProducts(filteredProducts);

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Producten</h1>
          <p className="text-gray-500 text-sm mt-1">{products.length} producten in {grouped.length} groepen</p>
        </div>
        <Button onClick={handleAdd} className="bg-[#E31E24] hover:bg-[#c91a1f] text-white">
          <Plus className="h-4 w-4 mr-2" /> Nieuw Product
        </Button>
      </div>

      {/* Category filter */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setFilterCategory('all')} className={`px-3 py-1.5 text-xs font-medium transition-all ${filterCategory === 'all' ? 'bg-[#E31E24] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Alles ({products.length})
        </button>
        {categories.map(c => (
          <button key={c.id} onClick={() => setFilterCategory(c.id)} className={`px-3 py-1.5 text-xs font-medium transition-all ${filterCategory === c.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {c.name_nl}
          </button>
        ))}
      </div>

      {/* Grouped product table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-10"></TableHead>
              <TableHead className="w-16">Foto</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Categorie</TableHead>
              <TableHead className="w-20">Varianten</TableHead>
              <TableHead className="w-20 text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grouped.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">Geen producten</TableCell></TableRow>
            ) : grouped.map((group) => {
              const isExpanded = expandedGroups[group.base];
              const hasVariants = group.products.length > 1;
              const first = group.products[0];
              return (
                <React.Fragment key={group.base}>
                  {/* Group row */}
                  <TableRow className="hover:bg-gray-50 cursor-pointer" onClick={() => hasVariants && toggleGroup(group.base)}>
                    <TableCell className="w-10 text-center">
                      {hasVariants && (isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 inline" /> : <ChevronRight className="w-4 h-4 text-gray-400 inline" />)}
                    </TableCell>
                    <TableCell>
                      {first.image_url ? <img src={first.image_url} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 bg-gray-100 rounded" />}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-gray-900">{group.base}</span>
                      {hasVariants && <span className="ml-2 text-xs text-gray-400">({group.products.length}x)</span>}
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">{getCategoryName(first.category_id)}</TableCell>
                    <TableCell className="text-center text-sm text-gray-500">{group.products.length}</TableCell>
                    <TableCell className="text-right">
                      {!hasVariants && (
                        <>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(first); }}><Pencil className="h-4 w-4 text-gray-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(first); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded variants */}
                  {isExpanded && hasVariants && group.products.map((product) => {
                    const variant = (product.name_nl || '').replace(group.base, '').trim();
                    return (
                      <TableRow key={product.id} className="bg-gray-50/50 hover:bg-gray-50">
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell className="pl-8">
                          <span className="text-sm text-gray-700">{variant || product.name_nl}</span>
                          <span className={`ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${product.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {product.is_active ? 'Actief' : 'Inactief'}
                          </span>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(product)}><Pencil className="h-3.5 w-3.5 text-gray-500" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(product)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedProduct ? 'Product Bewerken' : 'Nieuw Product'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <ImageUploader value={formData.image_url} onChange={(url) => handleChange('image_url', url)} label="Product Afbeelding" />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Categorie *</Label>
                <Select value={formData.category_id} onValueChange={(v) => handleChange('category_id', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Selecteer categorie" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name_nl}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <div className="flex items-center gap-3">
                  <Switch checked={formData.is_active} onCheckedChange={(c) => handleChange('is_active', c)} />
                  <Label className="text-gray-700">Product actief</Label>
                </div>
              </div>
            </div>

            {/* Multilingual Content with AI translate */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b">
                <span className="font-semibold text-sm text-gray-700">Inhoud &amp; Vertalingen</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={translateAll}
                  disabled={translating || !formData.name_nl}
                  className="text-xs"
                >
                  <Languages className="h-3.5 w-3.5 mr-1.5" />
                  {translating ? 'Vertalen...' : 'AI Vertalen (NL → EN/FR/DE)'}
                </Button>
              </div>

              <div className="p-4">
                <Tabs defaultValue="nl" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="nl">Nederlands</TabsTrigger>
                    <TabsTrigger value="en">English</TabsTrigger>
                    <TabsTrigger value="fr">Français</TabsTrigger>
                    <TabsTrigger value="de">Deutsch</TabsTrigger>
                  </TabsList>

                  {[
                    { lang: 'nl', nameLabel: 'Naam *', namePh: 'Productnaam', descLabel: 'Beschrijving', descPh: 'Productbeschrijving...' },
                    { lang: 'en', nameLabel: 'Name', namePh: 'Product name', descLabel: 'Description', descPh: 'Product description...' },
                    { lang: 'fr', nameLabel: 'Nom', namePh: 'Nom du produit', descLabel: 'Description', descPh: 'Description du produit...' },
                    { lang: 'de', nameLabel: 'Name', namePh: 'Produktname', descLabel: 'Beschreibung', descPh: 'Produktbeschreibung...' },
                  ].map(({ lang, nameLabel, namePh, descLabel, descPh }) => (
                    <TabsContent key={lang} value={lang} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-gray-700">{nameLabel}</Label>
                        <Input value={formData[`name_${lang}`]} onChange={(e) => handleChange(`name_${lang}`, e.target.value)} placeholder={namePh} className="mt-1.5" required={lang === 'nl'} />
                      </div>
                      <div>
                        <Label className="text-gray-700">{descLabel}</Label>
                        <div className="mt-1.5">
                          <RichTextEditor
                            value={formData[`description_${lang}`]}
                            onChange={(v) => handleChange(`description_${lang}`, v)}
                            placeholder={descPh}
                          />
                        </div>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </div>

            {/* Specifications */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-gray-700">Specificaties</Label>
                <Button type="button" variant="outline" size="sm" onClick={addSpec}><Plus className="h-4 w-4 mr-1" /> Toevoegen</Button>
              </div>
              {formData.specifications.length === 0 ? (
                <p className="text-gray-500 text-sm">Geen specificaties.</p>
              ) : (
                <div className="space-y-2">
                  {formData.specifications.map((spec, i) => (
                    <div key={i} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                      <Input placeholder="Kenmerk (NL)" value={spec.key_nl} onChange={(e) => updateSpec(i, 'key_nl', e.target.value)} className="text-sm flex-1" />
                      <Input placeholder="Waarde" value={spec.value} onChange={(e) => updateSpec(i, 'value', e.target.value)} className="text-sm flex-1" />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSpec(i)} className="text-red-500"><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-[#E31E24] hover:bg-[#c91a1f] text-white">
                {saving ? 'Opslaan...' : 'Opslaan'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Product Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>Weet u zeker dat u "{selectedProduct?.name_nl}" wilt verwijderen?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
